import { useMemo, useCallback, useRef, useEffect } from 'react';
import { ThreeEvent } from '@react-three/fiber';
import { useFilterContext, useEventContext, useModeContext } from '@repo/front-end-state';
import { BufferGeometry, MeshBasicMaterial } from 'three';
import { createFilterPathFromLineage } from '@repo/front-end-utils';
import { AppMode, Entity, EntityHoverSelection } from '@repo/frontend-types';
import { TwinEntity } from '@repo/backend-types';
import { getSelectionCriteria } from '@repo/ui';

interface Props {
  entity: Entity;
  geometry: BufferGeometry;
  setHover: (hover: boolean) => void;
  setPressed: (hover: boolean) => void;
  mapDiv: HTMLDivElement;
  lineage: TwinEntity[];
}

const dummyMaterial = new MeshBasicMaterial();
const HOVER_DELAY_MS = 200;

function getMaxTwinDepthObject(e: ThreeEvent<PointerEvent> | ThreeEvent<MouseEvent>) {
  let maxDepthObj = e.intersections[0].object;

  e.intersections.forEach(intersection => {
    let currentDepth = intersection.object.userData.depth || 0;
    let maxDepth = maxDepthObj.userData.depth || 0;

    if (currentDepth > maxDepth) {
      maxDepthObj = intersection.object;
    }
  });

  return maxDepthObj;
}

const setTrueIfMaxDepthEntity = (
  e: ThreeEvent<PointerEvent>,
  mapDiv: HTMLDivElement,
  currentHovered3DEntity: EntityHoverSelection | null,
  setState: (state: boolean) => void,
  set3DState: (state: any) => void,
  shouldApplyHover: boolean = true,
) => {
  const maxDepthObj = getMaxTwinDepthObject(e);

  const hoveredEntity = e.object.userData as Entity;
  const maxDepthEntity = maxDepthObj.userData as Entity;

  const hoveredDepth = hoveredEntity.depth ?? 0;
  const maxDepth = maxDepthEntity.depth ?? 0;

  if (
    (hoveredDepth > maxDepth || hoveredEntity.id === maxDepthEntity.id) &&
    hoveredEntity.id !== currentHovered3DEntity?.id &&
    shouldApplyHover
  ) {
    // to reduce the number of re-renders
    mapDiv.style.cursor = 'pointer';
    setState(true);
    set3DState({ id: hoveredEntity.id });
  } else {
    if (hoveredEntity.id !== maxDepthEntity.id || !shouldApplyHover) {
      setState(false);
    }
  }
};

export const Hitbox = ({ entity, geometry, mapDiv, setHover, setPressed, lineage }: Props) => {
  const { setFilter, filter, toggleEquipmentSelection } = useFilterContext();
  const { setCurrentHoveredEntityOn3DScene, currentHoveredEntityOn3DScene } = useEventContext();
  const { appMode } = useModeContext();

  // Determine if this specific entity is directly selected (not just a child of selected)
  const parent = lineage[lineage.length - 1];
  const { areaIsSelected } = useMemo(
    () => getSelectionCriteria(entity, parent, filter),
    [entity, parent, filter],
  );

  // Helper function to check if hover should be blocked for selected entities
  const shouldBlockHover = useCallback(() => {
    return areaIsSelected; // Block hover for directly selected entities only
  }, [areaIsSelected]);

  // Debounced hover implementation
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastEventRef = useRef<ThreeEvent<PointerEvent> | null>(null);

  // Debounced hover handler
  const debouncedSetHover = useCallback(
    (event: ThreeEvent<PointerEvent> | null, shouldHover: boolean) => {
      // Handle null event gracefully
      if (event === null) {
        setHover(false);
        setCurrentHoveredEntityOn3DScene(null);
        return;
      }

      // Clear existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (!shouldHover) {
        // Immediately clear hover when leaving
        setHover(false);
        setCurrentHoveredEntityOn3DScene(null);
        return;
      }

      // Check if this entity is directly selected - if so, don't apply hover
      if (shouldBlockHover()) {
        return;
      }

      // Store the event for the timeout
      lastEventRef.current = event;

      // Apply hover after delay - simplified version first
      hoverTimeoutRef.current = setTimeout(() => {
        if (lastEventRef.current) {
          // Double-check selection state at timeout execution
          if (shouldBlockHover()) {
            return;
          }

          setTrueIfMaxDepthEntity(
            lastEventRef.current,
            mapDiv,
            currentHoveredEntityOn3DScene,
            setHover,
            setCurrentHoveredEntityOn3DScene,
            true,
          );
        }
      }, HOVER_DELAY_MS); // Apply hover delay
    },
    [
      mapDiv,
      currentHoveredEntityOn3DScene,
      setHover,
      setCurrentHoveredEntityOn3DScene,
      shouldBlockHover,
    ],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const newGeo = useMemo(() => {
    const newGeo = geometry.clone();
    newGeo.scale(1, 1, 1);
    return newGeo;
  }, [geometry]);

  return (
    <mesh
      userData={entity}
      geometry={newGeo}
      visible={false}
      material={dummyMaterial}
      onPointerDown={e => {
        if (e.button === 0) {
          setTrueIfMaxDepthEntity(
            e,
            mapDiv,
            currentHoveredEntityOn3DScene,
            setPressed,
            setCurrentHoveredEntityOn3DScene,
          );
        }
      }}
      onPointerUp={() => {
        setPressed(false);
      }}
      onPointerCancel={() => {
        setPressed(false);
      }}
      onClick={e => {
        // N.B. this is twin-heirachy depth, not scene depth
        const maxDepthObj = getMaxTwinDepthObject(e);

        let maxDepthEntity = maxDepthObj.userData as TwinEntity;
        let maxDepthId = maxDepthEntity.id;

        // this test ensures that only one of the clicked-on entities (the one deepest in the twin) calls setState
        if (maxDepthId === entity.id) {
          if (appMode === AppMode.EQUIPMENT) {
            // Equipment mode multi-selects: clicking a piece of equipment TOGGLES it in/out of the
            // selection (a FilterType.ASSET pill) rather than replacing the spatial filter path.
            // Non-asset geometry is a no-op — areas aren't rendered in Equipment mode
            // (equipment-mode-panel FR9).
            if (entity.type?.name?.toLowerCase() === 'asset') {
              toggleEquipmentSelection({ id: entity.id, bId: entity.bID, label: entity.name });
            }
          } else {
            const filterPath = createFilterPathFromLineage([...lineage, entity]);

            // Update filter in filterContext
            setFilter(filterPath);
          }
        }
      }}
      onPointerEnter={e => {
        // Check if this entity is directly selected - if so, don't apply hover
        if (shouldBlockHover()) {
          return;
        }

        debouncedSetHover(e, true);
      }}
      onPointerMove={e => {
        // Check if this entity is directly selected - if so, don't process move events
        if (shouldBlockHover()) {
          return;
        }

        // Update the stored event for the debounced hover
        lastEventRef.current = e;
      }}
      onPointerLeave={() => {
        // Don't clear hover state for directly selected entities to prevent refresh
        if (!shouldBlockHover()) {
          // Only clear hover for non-selected entities
          debouncedSetHover(null, false);
        }

        setPressed(false);
        mapDiv.style.cursor = 'grab';
      }}
    />
  );
};
